import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";

/** yyyy-MM-dd as a local date (never `new Date("2026-10-26")`, which is UTC midnight and reads as the day before west of Greenwich). */
function toDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/** Today as yyyy-MM-dd, in the viewer's own calendar. */
export function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

/**
 * The effective date of a scenario: the same "As of" control Monitor and Capacity have, as a yyyy-MM-dd string. The scenario is studied
 * against the portfolio as it stood on that day (today or any earlier one), so its Monitor and Capacity are the real ones' for that date
 * with the moves applied.
 */
export function ScenarioAsOfPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const selected = toDate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-2" disabled={disabled} aria-label="Effective date">
          <CalendarIcon className="h-3.5 w-3.5" />
          As of <span suppressHydrationWarning>{format(selected, "d MMM yyyy")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => d && onChange(format(d, "yyyy-MM-dd"))}
          defaultMonth={selected}
          captionLayout="dropdown-buttons"
          fromYear={2015}
          toYear={new Date().getFullYear()}
          disabled={{ after: new Date() }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** When the data has no file for the day picked (a weekend, a holiday, before the first one), the server answers from the nearest earlier
 *  file: say so, instead of leaving the figures looking like the picked day's. Null when they are the same day. */
export function asOfNote(picked: string, resolved: string | undefined): string | null {
  if (!resolved || resolved === picked) return null;
  return `The Financed Amount and Contract Count of the scenario represents the status of contracts as ${format(toDate(resolved), "d MMM yyyy")}`;
}
